import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { THEME, shadows } from '../constants/theme';
import InteractionChart from './InteractionChart';
import {
  ClockIcon,
  EyeIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChatBubbleLeftIcon,
  DocumentTextIcon,
  UsersIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { Line, Bar } from 'react-chartjs-2';

const StatCard = ({ title, value, icon }) => (
  <div className="bg-white rounded-xl p-4" style={{ boxShadow: shadows.md }}>
    <div className="flex items-center space-x-3">
      <div className="p-2 rounded-lg" style={{ backgroundColor: `${THEME.primary}20` }}>
        {icon}
      </div>
      <div>
        <p className="text-sm" style={{ color: THEME.subtext }}>{title}</p>
        <p className="text-2xl font-bold" style={{ color: THEME.text }}>{value}</p>
      </div>
    </div>
  </div>
);

const InteractionDetails = ({ publicLinkId, linkType }) => {
  const [loading, setLoading] = useState(true);
  const [interactions, setInteractions] = useState([]);
  const [stats, setStats] = useState({
    totalViews: 0,
    uniqueVisitors: 0,
    avgTimeSpent: 0,
    downloads: 0,
    comments: 0
  });

  useEffect(() => {
    if (publicLinkId) {
      fetchInteractions();
    }
  }, [publicLinkId]);

  const fetchInteractions = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_interactions', {
        p_public_link_id: publicLinkId
      });

      if (error) throw error;

      setInteractions(data);
      calculateStats(data);
    } catch (error) {
      console.error('Error fetching interactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const views = data.filter(i => i.event_type === 'view');
    const uniqueSessions = new Set(data.map(i => i.session_id)).size;
    const timeSpentEvents = data.filter(i => i.event_type === 'time_spent');
    const downloads = data.filter(i => i.event_type === 'pdf_download').length;
    const comments = data.filter(i => i.event_type === 'comment').length;

    // Calculate average time spent
    const totalTimeSpent = timeSpentEvents.reduce((acc, curr) => {
      return acc + (curr.metadata?.duration || 0);
    }, 0);
    const avgTimeSpent = timeSpentEvents.length > 0 
      ? Math.round(totalTimeSpent / timeSpentEvents.length) 
      : 0;

    setStats({
      totalViews: views.length,
      uniqueVisitors: uniqueSessions,
      avgTimeSpent,
      downloads,
      comments
    });
  };

  const getEventTypeDisplay = (event) => {
    switch (event.event_type) {
      case 'view':
        const device = event.user_agent?.toLowerCase().includes('mobile') ? 'Mobile' : 'Desktop';
        return `Viewed on ${device}`;
      case 'pdf_download':
        return 'Downloaded PDF';
      case 'section_viewed':
        return `Viewed ${event.metadata?.section_name?.replace(/-/g, ' ').replace(/(^\w|\s\w)/g, l => l.toUpperCase())} section`;
      case 'accept':
        return 'Accepted';
      case 'decline':
        return 'Declined';
      case 'comment':
        return 'Added comment';
      default:
        return null;
    }
  };

  const getRecentActivity = () => {
    return interactions
      .filter(event => {
        const eventType = getEventTypeDisplay(event);
        return eventType !== null && event.event_type !== 'time_spent';
      })
      .slice(0, 10)
      .map(event => ({
        ...event,
        displayType: getEventTypeDisplay(event)
      }));
  };

  const getViewsChartData = () => {
    const viewEvents = interactions
      .filter(i => i.event_type === 'view')
      .map(i => ({
        x: new Date(i.created_at),
        y: 1,
        device: i.user_agent?.toLowerCase().includes('mobile') ? 'Mobile' : 'Desktop'
      }));

    return viewEvents;
  };

  const getSectionViewsData = () => {
    const sectionViews = interactions.filter(i => i.event_type === 'section_viewed');
    const sectionCounts = {};

    sectionViews.forEach(view => {
      const sectionName = view.metadata?.section_name || 'unknown';
      sectionCounts[sectionName] = (sectionCounts[sectionName] || 0) + 1;
    });

    return Object.entries(sectionCounts).map(([section, count]) => ({
      x: section.replace('-', ' ').replace(/(^\w|\s\w)/g, l => l.toUpperCase()),
      y: count
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-t-transparent" style={{ borderColor: `${THEME.primary} transparent` }} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Views"
          value={stats.totalViews}
          icon={<EyeIcon className="w-5 h-5" />}
        />
        <StatCard
          title="Unique Visitors"
          value={stats.uniqueVisitors}
          icon={<UsersIcon className="w-5 h-5" />}
        />
        <StatCard
          title="Downloads"
          value={stats.downloads}
          icon={<ArrowDownTrayIcon className="w-5 h-5" />}
        />
        <StatCard
          title="Comments"
          value={stats.comments}
          icon={<ChatBubbleLeftIcon className="w-5 h-5" />}
        />
      </div>

      {/* Views Chart */}
      <div className="bg-white rounded-xl p-6" style={{ boxShadow: shadows.md }}>
        <h3 className="text-lg font-semibold mb-4" style={{ color: THEME.text }}>
          Views Over Time
        </h3>
        <div className="h-64">
          <Line
            data={{
              datasets: [
                {
                  label: 'Desktop Views',
                  data: getViewsChartData().filter(d => d.device === 'Desktop'),
                  borderColor: THEME.primary,
                  backgroundColor: `${THEME.primary}20`,
                  fill: true,
                },
                {
                  label: 'Mobile Views',
                  data: getViewsChartData().filter(d => d.device === 'Mobile'),
                  borderColor: THEME.secondary,
                  backgroundColor: `${THEME.secondary}20`,
                  fill: true,
                }
              ]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                x: {
                  type: 'time',
                  time: {
                    unit: 'day'
                  },
                  title: {
                    display: true,
                    text: 'Date'
                  }
                },
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Views'
                  },
                  ticks: {
                    stepSize: 1
                  }
                }
              }
            }}
          />
        </div>
      </div>

      {/* Section Views Chart */}
      <div className="bg-white rounded-xl p-6" style={{ boxShadow: shadows.md }}>
        <h3 className="text-lg font-semibold mb-4" style={{ color: THEME.text }}>
          Section Views
        </h3>
        <div className="h-64">
          <Bar
            data={{
              datasets: [{
                data: getSectionViewsData(),
                backgroundColor: THEME.primary,
              }]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Views'
                  },
                  ticks: {
                    stepSize: 1
                  }
                }
              }
            }}
          />
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl p-6" style={{ boxShadow: shadows.md }}>
        <h3 className="text-lg font-semibold mb-4" style={{ color: THEME.text }}>
          Recent Activity
        </h3>
        <div className="space-y-4">
          {getRecentActivity().map((activity, index) => (
            <div
              key={index}
              className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
            >
              <div>
                <p className="font-medium" style={{ color: THEME.text }}>
                  {activity.displayType}
                </p>
                <p className="text-sm" style={{ color: THEME.subtext }}>
                  {new Date(activity.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InteractionDetails; 